import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { AddressSelector } from '../components/AddressBook';
import { useBranding } from '../hooks/useBranding';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Trash2, Upload, X, ShoppingCart } from 'lucide-react';

const ALLOWED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.tiff,.tif,.svg,.bmp,.ai,.eps,.doc,.docx,.xls,.xlsx';

function SampleFileRow({ cartItemId, files, onFilesChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files);
    onFilesChange(cartItemId, [...files, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (idx) => {
    onFilesChange(cartItemId, files.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
          <Upload size={13} /> Add Sample Files
          <input type="file" multiple accept={ALLOWED_EXTENSIONS} onChange={handleFiles} style={{ display: 'none' }} />
        </label>
        <span className="text-muted text-sm">PDF, JPG, PNG, AI, EPS, DOC, etc.</span>
      </div>
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {files.map((f, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'var(--gray-100)', border: '1px solid var(--gray-300)',
              borderRadius: 4, padding: '2px 8px', fontSize: 12,
            }}>
              {f.name}
              <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 0 }}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CartPage() {
  const { items, updateItem, removeItem, clearCart, total } = useCart();
  // v1.34.0: the server prices the whole cart, because answer-driven charges are
  // levied per order rather than per line and cannot be summed line by line.
  const [pricing, setPricing] = useState(null);
  const cartKey = JSON.stringify(items.map(i => [i.product_id, i.quantity, i.attributes]));
  useEffect(() => {
    if (!items.length) { setPricing(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.post('/api/orders/price-cart', {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, attributes: i.attributes || {} })),
      })
        .then(r => { if (!cancelled) setPricing(r.data); })
        .catch(() => { if (!cancelled) setPricing(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [cartKey, items]);

  // Fall back to the client-side sum if the server call has not landed yet, so
  // the page never shows a blank where a number should be.
  const grandTotal = pricing ? pricing.total : total;
  const extraCharges = pricing?.charges || [];
  const { defaultProductImage } = useBranding();
  const imgOf = (p) => p?.image_url || defaultProductImage || '/default-ticket.svg';
  const { user } = useAuth();
  const nav = useNavigate();
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [form, setForm] = useState({ po_number: '', notes: '', contact_phone: '' });
  const [sampleFiles, setSampleFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [maxLines, setMaxLines] = useState(10);
  const [cust, setCust] = useState({ po_field_label: 'PO Number', po_field_required: 0, order_image_mode: 'none' });
  const [addresses, setAddresses] = useState([]);
  const [freight, setFreight] = useState({ inside_delivery: false, lift_gate: false });

  // Pre-select user's default address
  useEffect(() => {
    if (user?.default_address_id) setSelectedAddressId(user.default_address_id);
  }, [user]);

  // Pre-fill the contact phone from the user's default (they can edit it).
  useEffect(() => {
    if (user?.phone) setForm(f => (f.contact_phone ? f : { ...f, contact_phone: user.phone }));
  }, [user]);

  // Load customer settings + addresses
  useEffect(() => {
    const cid = user?.customer_id;
    if (!cid) return;
    api.get(`/api/customers/${cid}`).then(cr => {
      const c = cr.data.customer || {};
      setMaxLines(c.max_lines_per_order || 10);
      setCust({
        po_field_label: c.po_field_label || 'PO Number',
        po_field_required: c.po_field_required || 0,
        order_image_mode: c.order_image_mode || 'none',
      });
    }).catch(() => {});
    api.get(`/api/addresses/customers/${cid}/addresses`)
      .then(r => setAddresses(r.data.addresses || r.data || []))
      .catch(() => {});
  }, [user]);

  // Default the freight flags from whichever address is selected
  useEffect(() => {
    const a = addresses.find(x => x.id === selectedAddressId);
    if (a) setFreight({ inside_delivery: !!a.inside_delivery, lift_gate: !!a.lift_gate });
  }, [selectedAddressId, addresses]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSampleFiles = (productId, files) => {
    setSampleFiles(prev => ({ ...prev, [productId]: files }));
  };

  const handleQtyChange = (productId, newQty) => {
    const qty = parseInt(newQty);
    if (!isNaN(qty) && qty > 0) updateItem(productId, { quantity: qty });
  };

  const submit = async () => {
    if (!items.length) return toast.error('Cart is empty');
    if (!selectedAddressId) return toast.error('Please select a ship-to address');
    if (cust.po_field_required && !String(form.po_number).trim()) {
      return toast.error(`${cust.po_field_label} is required`);
    }
    if (!String(form.contact_phone).trim()) {
      return toast.error('A contact phone is required');
    }
    if (items.length > maxLines) {
      return toast.error(`Maximum ${maxLines} line${maxLines === 1 ? '' : 's'} per order. Please remove items or start a new order.`);
    }
    // A quoted item cannot be made without a sample to match — the add-ons
    // sheet marks the sample "a must" on all 27 rows.
    //
    // v1.48.0 tried to enforce that by marking an "Upload sample" QUESTION
    // required. That could never work: attribute questions have no file type,
    // so it became a required text box that no file could satisfy and it
    // blocked checkout outright. The requirement belongs here, next to the
    // real uploader, where the customer can actually meet it.
    // Cart lines nest the product: { product_id, product, quantity, ... }.
    //
    // A NEW job also needs a sample: "that way we know what is supposed to be
    // on the item". A reorder does not — the artwork already exists.
    //
    // The order type is answered on the line, and the wording differs between
    // question sets ("New" on the LAZ sets, "New Job" on the add-ons), so this
    // matches the start of the answer rather than a fixed list.
    const ORDER_CODES = ['type_of_order', 'order_type', 'type_of_order_info'];
    const isNewJob = (attrs) => ORDER_CODES.some(
      c => /^new\b/i.test(String(attrs?.[c] || '').trim())
    );

    const missingSample = items.filter((i) => {
      if (sampleFiles[i.product_id]?.length) return false;
      return i.product?.quote_required || isNewJob(i.attributes);
    });
    if (missingSample.length) {
      const names = missingSample.map(i => i.product?.name || i.product?.sku).join(', ');
      const why = missingSample.every(i => i.product?.quote_required)
        ? 'priced by hand'
        : 'a new job';
      return toast.error(
        missingSample.length === 1
          ? `${names} is ${why}, so it needs a sample. Use "Add sample" on that line.`
          : `${missingSample.length} items need a sample: ${names}. Use "Add sample" on each line.`
      );
    }
    setSubmitting(true);
    try {
      const r = await api.post('/api/orders', {
        address_id: selectedAddressId,
        po_number: form.po_number,
        notes: form.notes,
        contact_phone: form.contact_phone,
        inside_delivery: freight.inside_delivery,
        lift_gate: freight.lift_gate,
        items: items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          attributes: i.attributes,
          notes: i.notes || '',
          image_provided: (sampleFiles[i.product_id]?.length || 0) > 0,
        }))
      });
      const order = r.data.order;

      // 2. Upload sample files for each line item
      const orderDetail = await api.get(`/api/orders/${order.id}`);
      const orderItems = orderDetail.data.items;

      for (const cartItem of items) {
        const files = sampleFiles[cartItem.product_id] || [];
        if (!files.length) continue;
        const orderItem = orderItems.find(oi => oi.product_id === cartItem.product_id);
        if (!orderItem) continue;

        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        await api.post(`/api/orders/${order.id}/items/${orderItem.id}/files`, fd);
      }

      clearCart();
      setSampleFiles({});
      toast.success(`Order ${order.order_number} placed!`);
      nav(`/orders/${order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to place order');
    } finally { setSubmitting(false); }
  };

  if (!items.length) return (
    <>
      <div className="topbar"><h2>Cart</h2></div>
      <div className="page">
        <div className="empty-state">
          <ShoppingCart size={48} />
          <p>Your cart is empty.</p>
          <Link to="/catalog" className="btn btn-primary mt-16">Browse Products</Link>
        </div>
      </div>
    </>
  );

  const overLimit = items.length > maxLines;

  return (
    <>
      <div className="topbar"><h2>Cart</h2></div>
      <div className="page">
        {overLimit && (
          <div className="alert alert-error mb-16">
            This order has {items.length} items but the maximum is {maxLines} per order.
            Please remove {items.length - maxLines} item{items.length - maxLines > 1 ? 's' : ''} or complete this order and start a new one.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20 }}>
          <div>
            <div className="card mb-16">
              <div className="card-header">
                <h3>Items ({items.length} / {maxLines} max)</h3>
              </div>
              {items.map((item, idx) => (
                <div key={item.product_id} style={{
                  padding: '16px 0',
                  borderBottom: idx < items.length - 1 ? '1px solid var(--gray-100)' : 'none'
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <img src={imgOf(item.product)} alt=""
                      style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div className="fw-600">{item.product.name}</div>
                      {Object.entries(item.attributes || {}).map(([k, v]) =>
                        v ? <div key={k} className="text-sm text-muted">{k}: {v}</div> : null
                      )}
                    </div>

                    {/* Qty editor */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label className="text-sm text-muted">
                        {item.product?.price_unit === 'per_case' ? 'Cases:' : 'Qty:'}
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        style={{ width: 110 }}
                        value={item.quantity}
                        min={item.product?.price_unit === 'per_case' ? 1 : 1000}
                        step={item.product?.price_unit === 'per_case' ? 1 : 5000}
                        onChange={e => handleQtyChange(item.product_id, e.target.value)}
                      />
                    </div>

                    <div style={{ textAlign: 'right', minWidth: 90 }}>
                      {/* v1.44.0 — an item quoted by hand has no price. Showing
                           $0.00 here would read as free. */}
                      {(pricing?.lines || []).find(l => l.product_id === item.product_id)?.quote_required ? (
                        <div className="text-sm" style={{ color: 'var(--brand-red)', maxWidth: 160 }}>
                          Toledo Ticket to provide custom pricing
                        </div>
                      ) : (
                        <>
                          <div className="fw-600">
                            ${(item.product?.price_unit === 'per_case'
                              ? item.quantity * item.pricePerM
                              : (item.quantity / 1000) * item.pricePerM
                            ).toFixed(2)}
                          </div>
                          <div className="text-sm text-muted">
                            ${item.pricePerM.toFixed(2)}/{item.product?.price_unit === 'per_case'
                              ? (item.product?.qty_unit_label || 'case').toLowerCase().replace(/s$/,'')
                              : 'M'}
                          </div>
                        </>
                      )}
                    </div>

                    <button className="btn btn-ghost btn-sm" onClick={() => removeItem(item.product_id)} title="Remove">
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {/* Item notes */}
                  <div style={{ marginTop: 8 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Line item notes (optional)"
                      value={item.notes || ''}
                      onChange={e => updateItem(item.product_id, { notes: e.target.value })}
                    />
                  </div>

                  {/* Sample file uploads */}
                  <SampleFileRow
                    cartItemId={item.product_id}
                    files={sampleFiles[item.product_id] || []}
                    onFilesChange={handleSampleFiles}
                  />
                </div>
              ))}

              <div style={{ padding: '12px 0 0', borderTop: '1px solid var(--gray-200)', marginTop: 8 }}>
                {extraCharges.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span className="text-muted">Items</span>
                      <span>${Number(pricing.items_total).toFixed(2)}</span>
                    </div>
                    {extraCharges.map(c => (
                      <div key={c.charge_type_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span className="text-muted">
                          {c.label}
                          {c.unit !== 'flat' && c.quantity != null && c.unit_rate != null
                            ? ` (${Number(c.quantity).toLocaleString()} × $${Number(c.unit_rate).toFixed(2)})` : ''}
                        </span>
                        <span>${Number(c.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {pricing?.unpriced?.length > 0 && (
                  <p className="form-hint" style={{ textAlign: 'right', color: 'var(--brand-red)' }}>
                    {pricing.unpriced.map(u => u.label).join(', ')} — not yet priced for your account.
                    Toledo Ticket will confirm{pricing.unpriced.length === 1 ? ' it' : ' them'}.
                  </p>
                )}
                {/* v1.42.0 — say it here, where it can still be fixed, rather
                    than letting Place Order be the first mention of it. */}
                {pricing?.qty_problems?.length > 0 && pricing.qty_problems.map((q, i) => (
                  <p key={i} className="form-hint" style={{ textAlign: 'right', color: 'var(--brand-red)' }}>
                    {q.error}
                  </p>
                ))}
                {pricing?.needs_quote && (
                  <p className="form-hint" style={{ textAlign: 'right', color: 'var(--brand-red)' }}>
                    {pricing.quote_lines.map(q => q.name).join(', ')} —
                    {pricing.quote_lines.length === 1 ? ' is' : ' are'} not in the total.
                    Toledo Ticket will price {pricing.quote_lines.length === 1 ? 'it' : 'them'} and confirm.
                  </p>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <span className="fw-600" style={{ fontSize: 16 }}>
                    {pricing?.needs_quote ? 'Total so far: ' : 'Total: '}${grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Shipping & Order Info</h3></div>
              {user?.customer_id ? (
                <div className="form-group">
                  <AddressSelector
                    customerId={user.customer_id}
                    value={selectedAddressId}
                    onChange={setSelectedAddressId}
                    allowAdd={true}
                  />
                </div>
              ) : (
                // Ship-tos belong to a customer, and a Toledo Ticket staff
                // account has none — so this section used to render as empty
                // space, which reads as a missing feature rather than a
                // missing customer. Say which it is.
                <div className="alert alert-info" style={{margin:'0 0 12px'}}>
                  <strong>No ship-to list because you are signed in as Toledo Ticket staff.</strong>
                  <div style={{marginTop:6}}>
                    Shipping addresses belong to a customer. Go to Admin → Users and use
                    Impersonate on one of their users to place or test an order with their
                    addresses.
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    {cust.po_field_label}{cust.po_field_required ? <span className="required"> *</span> : null}
                  </label>
                  <input className="form-input" value={form.po_number} onChange={e => set('po_number', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact phone<span className="required"> *</span></label>
                  <input className="form-input" type="tel" value={form.contact_phone}
                    onChange={e => set('contact_phone', e.target.value)} placeholder="(555) 555-1234" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Delivery options</label>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', paddingTop: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={freight.inside_delivery}
                      onChange={e => setFreight(f => ({ ...f, inside_delivery: e.target.checked }))} />
                    Inside Delivery
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={freight.lift_gate}
                      onChange={e => setFreight(f => ({ ...f, lift_gate: e.target.checked }))} />
                    Lift Gate Service
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Order Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
          </div>

          <div>
            <div className="card">
              <div className="card-header"><h3>Order Summary</h3></div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                  <span className="text-muted">Lines</span>
                  <span style={{ color: overLimit ? 'var(--red)' : undefined }}>{items.length} / {maxLines}</span>
                </div>
                {extraCharges.length > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                      <span className="text-muted">Items</span><span>${Number(pricing.items_total).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                      <span className="text-muted">Additional charges</span><span>${Number(pricing.charges_total).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700, fontSize: 18 }}>
                  <span>{pricing?.needs_quote ? 'Total so far' : 'Total'}</span><span>${grandTotal.toFixed(2)}</span>
                </div>
              </div>
              <button
                className="btn btn-gold btn-lg"
                style={{ width: '100%' }}
                onClick={submit}
                disabled={submitting || overLimit}
              >
                {submitting ? 'Placing Order…' : 'Place Order'}
              </button>
              <Link to="/catalog" className="btn btn-secondary btn-sm mt-8" style={{ display: 'block', textAlign: 'center' }}>
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
